import { useEffect, useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { useT } from '@/lib/i18n/useT';
import { cn } from '@/lib/cn';
import { CHECKER_BG } from '@/lib/images/checkerBackground';
import { prettyName, type LibraryItem } from './libraryItems';

/**
 * CH-17 · La biblioteca se abre ENCIMA del formulario, no dentro de la columna
 * que la invoca. Dentro de media columna de un diálogo de 640 px cada logo
 * salía a 20 px y no se reconocía ninguno. Aquí las teselas miden ≥ 112 px,
 * llevan el nombre debajo, se buscan por nombre y crecen al pasar el ratón.
 *
 * Es presentacional: recibe los ítems y devuelve la URL elegida. Elegir cierra.
 * CH-14 reutilizará esto mismo para las siluetas de merch, con `group`.
 */
export function LibraryPickerDialog({
  open,
  onOpenChange,
  title,
  description,
  items,
  value,
  onPick,
  isLoading,
  emptyLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  items: LibraryItem[];
  value: string | null;
  onPick: (url: string) => void;
  isLoading?: boolean;
  emptyLabel?: string;
}) {
  const t = useT();
  const [q, setQ] = useState('');
  useEffect(() => {
    if (open) setQ('');
  }, [open]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (i) => prettyName(i.name).toLowerCase().includes(needle) || (i.group ?? '').toLowerCase().includes(needle),
    );
  }, [items, q]);

  // Grupos en orden de aparición; sin `group` todo va en uno sin cabecera.
  const groups = useMemo(() => {
    const m = new Map<string, LibraryItem[]>();
    for (const it of shown) {
      const k = it.group ?? '';
      (m.get(k) ?? m.set(k, []).get(k)!).push(it);
    }
    return [...m.entries()];
  }, [shown]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description ?? t('stock.library.count', { n: items.length })}</DialogDescription>
        </DialogHeader>

        <div className="relative mt-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
          <input
            autoFocus
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('stock.library.search')}
            aria-label={t('stock.library.search')}
            className="h-10 w-full rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-input)] pl-9 pr-3 text-sm text-[var(--color-fg-primary)] focus:border-[var(--color-accent-ink)] focus:outline-none"
          />
        </div>

        <div className="mt-3 max-h-[60vh] space-y-4 overflow-y-auto pr-1 scrollbar-thin">
          {isLoading ? (
            <p className="py-6 text-center text-xs text-[var(--color-fg-muted)]">{t('common.loading')}</p>
          ) : items.length === 0 ? (
            <p className="rounded-lg border border-dashed border-[var(--color-border-default)] px-4 py-8 text-center text-xs text-[var(--color-fg-muted)]">
              {emptyLabel ?? t('stock.library.empty')}
            </p>
          ) : shown.length === 0 ? (
            <p className="py-6 text-center text-xs text-[var(--color-fg-muted)]">{t('stock.library.noMatch', { q })}</p>
          ) : (
            groups.map(([group, list]) => (
              <section key={group || '_'}>
                {group ? (
                  <h4 className="mb-2 text-[11px] font-extrabold uppercase tracking-wide text-[var(--color-fg-muted)]">{group}</h4>
                ) : null}
                <ul
                  role="listbox"
                  aria-label={group || title}
                  className="grid grid-cols-[repeat(auto-fill,minmax(112px,1fr))] gap-3"
                >
                  {list.map((item) => {
                    const selected = value === item.url;
                    const label = prettyName(item.name);
                    return (
                      <li key={item.url}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={selected}
                          title={label}
                          onClick={() => {
                            onPick(item.url);
                            onOpenChange(false);
                          }}
                          className={cn(
                            'group flex w-full flex-col items-stretch gap-1.5 rounded-xl border p-2 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-xk-accent)]',
                            selected
                              ? 'border-[var(--color-accent-ink)] ring-2 ring-[var(--color-xk-accent)]'
                              : 'border-[var(--color-border-default)] hover:border-[var(--color-accent-ink)]',
                          )}
                        >
                          <span
                            className="relative grid aspect-square place-items-center overflow-hidden rounded-lg"
                            style={CHECKER_BG}
                          >
                            <img
                              src={resolveImageUrl(item.url)}
                              alt=""
                              loading="lazy"
                              className="max-h-[82%] max-w-[82%] object-contain transition-transform duration-200 group-hover:scale-125"
                            />
                            {selected && (
                              <Check className="absolute right-1 top-1 h-4 w-4 rounded-full bg-[var(--color-xk-accent)] p-0.5 text-[var(--color-fg-on-accent)]" />
                            )}
                          </span>
                          <span className="truncate text-center text-[11px] font-semibold text-[var(--color-fg-muted)]">{label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
